import classNames from 'classnames';
import { onReject } from 'common/utils/Bugsnag';
import React from 'react';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

export interface SearchResult {
    address: string;
    city: string;
    country: string;
    state: string;
    zipCode: string;
    latitude: number;
    longitude: number;
}

interface SearchOptions {
    componentRestrictions: { country: string[] };
}

interface Props {
    placeholder: string;
    error: boolean;
    initialAddress: string;
    name: string;
    onChange: (value: SearchResult) => void;
}

interface State {
    address: string;
}

export default class LocationSearchInput extends React.Component<Props, State> {
    private searchOptions: SearchOptions;

    constructor(props: Props, state: State) {
        super(props, state);
        this.state = { address: props.initialAddress };
        this.searchOptions = {
            componentRestrictions: {
                country: ['us'],
            },
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleSelect = this.handleSelect.bind(this);
        this.handleErrors = this.handleErrors.bind(this);
    }

    render() {
        return (
            <PlacesAutocomplete
                value={this.state.address}
                onChange={this.handleChange}
                onSelect={this.handleSelect}
                onError={this.handleErrors}
                searchOptions={this.searchOptions}
                debounce={500}
            >
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <div
                        className={classNames('location-input', {
                            'location-input--expanded': getInputProps()['aria-expanded'],
                            'location-input--error': this.props.error,
                        })}
                    >
                        <input
                            {...getInputProps({
                                placeholder: this.props.placeholder,
                                className: 'input-text input-width--max',
                                onBlur: () => {
                                    this.geocodeAndNotify(this.state.address);
                                },
                            }) as any}
                            name={this.props.name}
                        />
                        <div className="location-input__dropdown">
                            {loading && <div>Loading...</div>}
                            {suggestions.map(suggestion => {
                                const className = suggestion.active
                                    ? 'location-input-item--active'
                                    : 'location-input-item';
                                return (
                                    <div
                                        {...getSuggestionItemProps(suggestion, {
                                            className,
                                        }) as any}
                                    >
                                        <span>{suggestion.description}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </PlacesAutocomplete>
        );
    }

    private handleChange(address: string) {
        this.setState({ address });
        if (address === '') {
            this.props.onChange(this.emptySearchResult());
        }
    }

    private handleSelect(address: string) {
        this.setState({ address });
        this.geocodeAndNotify(address);
    }

    private handleErrors(status: string) {
        if (!['NOT_FOUND', 'OK', 'ZERO_RESULTS'].includes(status)) {
            onReject(`SmartShoot location autocomplete status: ${status}`);
        }
    }

    private geocodeAndNotify(address: string) {
        this.geocode(address)
            .then(result => this.props.onChange(result))
            .catch(err => onReject(err));
    }

    private async geocode(address: string): Promise<SearchResult> {
        const results = await geocodeByAddress(address);
        const latLng = await getLatLng(results[0]);
        return this.createGeocodeResult(results[0], latLng);
    }

    private createGeocodeResult(
        results: google.maps.GeocoderResult,
        latLng: google.maps.LatLngLiteral
    ): SearchResult {
        const addressComponents = {
            zipCode: '',
            country: '',
            state: '',
            city: '',
            streetNumber: '',
            route: '',
        };

        results.address_components.forEach(c => {
            if (c.types.includes('street_number')) {
                addressComponents.streetNumber = c.short_name;
            }
            if (c.types.includes('route')) {
                addressComponents.route = c.long_name;
            }
            if (c.types.includes('postal_code')) {
                addressComponents.zipCode = c.short_name;
            }
            if (c.types.includes('country')) {
                addressComponents.country = c.short_name;
            }
            if (c.types.includes('administrative_area_level_1')) {
                addressComponents.state = c.short_name;
            }
            if (c.types.includes('locality')) {
                addressComponents.city = c.long_name;
            }
            if (c.types.includes('sublocality') && addressComponents.city === '') {
                addressComponents.city = c.long_name;
            }
        });

        return {
            address: `${addressComponents.streetNumber} ${addressComponents.route}`.trim(),
            city: addressComponents.city,
            country: addressComponents.country,
            state: addressComponents.state,
            zipCode: addressComponents.zipCode,
            latitude: latLng.lat,
            longitude: latLng.lng,
        };
    }

    private emptySearchResult(): SearchResult {
        return {
            address: '',
            city: '',
            country: '',
            state: '',
            zipCode: '',
            latitude: 0,
            longitude: 0,
        };
    }
}
